import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiClientError } from '@/api/errors';

/**
 * Apply a backend validation error's field details onto a React Hook Form.
 * Returns the top-level message (if any) for a form-level alert. Unknown fields
 * are ignored so they can still surface via the returned message (spec §30, §36:
 * use real backend validation errors).
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  knownFields?: readonly (keyof T)[],
): string {
  if (!(error instanceof ApiClientError)) {
    return error instanceof Error ? error.message : 'Something went wrong';
  }

  const fieldErrors = error.fieldErrors;
  let matchedAny = false;
  for (const [field, message] of Object.entries(fieldErrors)) {
    if (!knownFields || knownFields.includes(field)) {
      setError(field as Path<T>, { type: 'server', message });
      matchedAny = true;
    }
  }

  // If every detail mapped to a field, don't also show a redundant banner.
  return matchedAny && error.details.length === Object.keys(fieldErrors).length
    ? ''
    : error.message;
}
